<template>
  <form id="company" @submit.prevent="validateAndSubmit">
    <div class="grid-row">
      <div class="small-12 large-6 columns">
        <h2>Bedrijfsgegevens</h2>
        <div class="formsectioncompound">
          <div class="jq_gridcontentarea">
            <div class="wm-field small-12 columns">
              <div id="id-label" class="wm-field-label">
                <label for="id" class="optional">Klantnummer</label>
              </div>
              <div class="wm-field-input">
                <input id="id" :value="company.reference" type="text" name="id" readonly="readonly">
              </div>
            </div>
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="organisationName"
              :$v="$v.company.organisationName"
              :value="company.organisationName"
              :required="true"
              :maxlength="64"
              :normalization-filter="filters.capitalize"
              :label="$t('companyForm.fieldName.organisationName') + ' *'"
              name="organisationName"
              placeholder=""
              @input="(v) => setCompanyProperty('organisationName', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="kvkNumber"
              :$v="$v.company.kvkNumber"
              :value="company.kvkNumber"
              :required="true"
              :maxlength="8"
              :label="$t('companyForm.fieldName.kvkNumber')"
              name="kvkNumber"
              placeholder=""
              @input="(v) => setCompanyProperty('kvkNumber', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="accountNumber"
              :$v="$v.company.accountNumber"
              :value="company.accountNumber"
              :required="true"
              :maxlength="34"
              :normalization-filter="filters.iban"
              name="accountNumber"
              label="IBAN"
              placeholder=""
              @input="(v) => setCompanyProperty('accountNumber', v)"
            />
          </div>
        </div>
      </div>
      <div class="small-12 large-6 columns">
        <h2>Contactpersoon</h2>
        <div class="formsectioncompound">
          <div class="jq_gridcontentarea">
            <initials-input-field
              id="firstName"
              :$v="$v.company.firstName"
              :value="company.firstName"
              :required="false"
              :maxlength="12"
              :normalization-filter="filters.initials"
              :custom-container-classes="['small-6', 'wm-field', 'columns']"
              name="firstName"
              label="Voorletter"
              @input="(v) => setCompanyProperty('firstName', v)"
            />
            <input-field
              id="surnamePrefix"
              :$v="$v.company.surnamePrefix"
              :value="company.surnamePrefix"
              :required="false"
              :maxlength="8"
              :custom-container-classes="['small-6', 'end', 'wm-field', 'columns']"
              name="surnamePrefix"
              label="Tussenvoegsel"
              @input="(v) => setCompanyProperty('surnamePrefix', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="surname"
              :$v="$v.company.surname"
              :value="company.surname"
              :required="false"
              :maxlength="50"
              :normalization-filter="filters.capitalize"
              name="surname"
              label="Achternaam"
              @input="(v) => setCompanyProperty('surname', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <select-field
              id="gender"
              :$v="$v.company.gender"
              :value="company.gender"
              :options="salutationOptions"
              :required="true"
              name="gender"
              label="Aanhef *"
              @change="(v) => setCompanyProperty('gender', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="department"
              :$v="$v.company.department"
              :value="company.department"
              :required="false"
              :maxlength="32"
              :normalization-filter="filters.capitalize"
              :label="$t('companyForm.fieldName.department')"
              name="department"
              @input="(v) => setCompanyProperty('department', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="function"
              :$v="$v.company.function"
              :value="company.function"
              :required="false"
              :maxlength="32"
              :normalization-filter="filters.capitalize"
              :label="$t('companyForm.fieldName.function')"
              name="function"
              @input="(v) => setCompanyProperty('function', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <intl-tel-input-field
              id="phoneNumber"
              ref="phoneNumber"
              :$v="$v.company.phoneNumber"
              :value="company.phoneNumber"
              :required="false"
              name="phoneNumber"
              label="Telefoonnummer 1"
              @input="(v) => setCompanyProperty('phoneNumber', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <intl-tel-input-field
              id="phoneNumber2"
              ref="phoneNumber2"
              :$v="$v.company.phoneNumber2"
              :value="company.phoneNumber2"
              :required="false"
              name="phoneNumber2"
              label="Telefoonnummer 2"
              @input="(v) => setCompanyProperty('phoneNumber2', v)"
            />
          </div>
          <div class="jq_gridcontentarea">
            <input-field
              id="emailAddress"
              :$v="$v.company.emailAddress"
              :value="company.emailAddress"
              :required="true"
              :maxlength="80"
              name="emailAddress"
              label="E-mailadres *"
              @input="(v) => setCompanyProperty('emailAddress', v)"
            />
          </div>
        </div>
      </div>
      <fieldset class="submit">
        <a href="#" class="button alt cancel-company-edit" @click.prevent="resetForm">Annuleren</a>
        <input id="submit-company-data" type="submit" name="submit-company-data" value="Opslaan">
      </fieldset>
    </div>
  </form>
</template>

<script>
import IntlTelInputField from './fields/IntlTelInputField'
import InputField from './fields/InputField'
import SelectField from './fields/SelectField'
import ValidationMessageList from './ValidationMessageList'
import * as companyMutationTypes from '../../store/modules/company/mutation-types'
import * as genderTypes from '../../types/gender'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { initials, surname, iban } from '../../validators'
import customFilters from '../../filters'
import InitialsInputField from './fields/InitialsInputField'
import ShowAlertErrorHandler from '../../mixins/show-alert-error-handler'

export default {
  name: 'CompanyContactDetailsForm',
  components: {
    InitialsInputField,
    IntlTelInputField,
    InputField,
    SelectField,
    ValidationMessageList
  },
  mixins: [ShowAlertErrorHandler],
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      salutationOptions: [
        { value: '', text: this.$t('generic.select') },
        { value: genderTypes.GENDER_MALE, text: this.$t('generic.gender.M') },
        { value: genderTypes.GENDER_FEMALE, text: this.$t('generic.gender.F') },
        { value: genderTypes.GENDER_UNKNOWN, text: this.$t('generic.gender.U') }
      ],
      filters: {
        capitalize: customFilters.capitalize,
        upperCase: customFilters.upperCase,
        iban: customFilters.iban,
        initials: customFilters.initials
      }
    }
  },
  validations () {
    return {
      company: {
        organisationName: {
          required,
          maxLength: maxLength(64)
        },
        kvkNumber: {
          maxLength: maxLength(8)
        },
        accountNumber: {
          maxLength: maxLength(34),
          iban
        },
        firstName: {
          maxLength: maxLength(12),
          initials
        },
        surnamePrefix: {
          maxLength: maxLength(8),
          surname
        },
        surname: {
          maxLength: maxLength(50),
          surname
        },
        gender: {
          required
        },
        department: {
          maxLength: maxLength(32)
        },
        function: {
          maxLength: maxLength(32)
        },
        phoneNumber: {
          phoneNumber: this.getCustomFieldValidation('phoneNumber')
        },
        phoneNumber2: {
          phoneNumber2: this.getCustomFieldValidation('phoneNumber2')
        },
        emailAddress: {
          required,
          maxLength: maxLength(80),
          email
        }
      }
    }
  },
  methods: {
    setCompanyProperty (property, value) {
      this.company[property] = value
    },
    validateAndSubmit () {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }
      this.submit()
    },
    validate () {
      this.$v.$touch()
      return !this.$v.$invalid
    },
    async submit () {
      try {
        await this.$store.dispatch('company/submitCompanyContactDetails', this.company)
        this.$store.commit(`company/${companyMutationTypes.SET_COMPANY_CONTACT_DETAILS}`, this.company)
        this.showAlert('Bedrijfs- en contactpersoon opgeslagen')
        this.$v.$reset()
      } catch (error) {
        if (error.validationErrorResult && error.validationErrorResult.validationErrors) {
          error.validationErrorResult.validationErrors.forEach(propertyError => {
            this.showAlert(this.$te(`companyForm.validation['${propertyError.code}']`)
              ? this.$t(
                `companyForm.validation['${propertyError.code}']`,
                { fieldName: this.$t(`companyForm.fieldName['${propertyError.path}']`) }
              ) : propertyError.message
            )
          })
        } else {
          this.showAlertForErrorAndRedirectToRoute(error, {}, {401: {'name': 'login'}})
        }
      }
    },
    resetForm () {
      this.$emit('cancel', {})
    },
    getCustomFieldValidation (fieldRef) {
      return (value) => this.$refs[fieldRef].validate()
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/forms/CompanyContactDetailsForm.vue